/**
 * sale-amount — แหล่งเดียวสำหรับคำนวณ "ยอดขาย" ของใบส่งของ/ใบบิล (ฝั่ง frontend)
 * คู่กับ backend App\Support\SaleAmount
 *
 * เหตุผล: column สำเร็จรูปใน DB (bill_before_vat / bill_value_added_tax / bill_grand_total)
 * เก็บไม่นิ่ง (บางใบ net หลังหักมัดจำ, บางใบ full, VAT ขาดบนมัดจำ, ปัดเพี้ยน, nettotal บางใบว่าง)
 * อย่าหยิบมาคิดเอง — ให้คิดจาก "ฐาน = nettotal − discount" (= ผลรวม items จริง)
 *
 *   tax_type 1 (VAT แยกนอก) → total = ฐาน × 1.07
 *   tax_type 2 (VAT รวมใน)  → total = ฐาน (ฐานรวม VAT แล้ว)
 *   tax_type 0 (ไม่มี VAT)   → total = ฐาน
 *
 * ⚠️ มัดจำ (deposit) ไม่ยุ่งกับ "ยอดขาย" — เป็นเงินจ่ายล่วงหน้า ใช้เฉพาะฝั่งค้างชำระ/รับเงิน
 */

const num = (v) => parseFloat(v || 0)

/** ฐานหลังหักส่วนลด (รองรับใบเก่าที่ nettotal ว่าง → fallback before_vat) */
export function saleBase (item) {
  const nettotal = num(item.bill_nettotal_amt)
  const discount = num(item.bill_discount_amt)
  if (nettotal > 0) return nettotal - discount
  return num(item.bill_before_vat) // fallback ใบเก่าที่ไม่ได้เก็บ nettotal
}

/** ยอดรวมก่อนหักส่วนลด */
export function saleSubtotal (item) {
  return saleBase(item) + num(item.bill_discount_amt)
}

/** ยอดก่อน VAT */
export function saleBeforeVat (item) {
  const base = saleBase(item)
  if (Number(item.bill_tax_type) === 2) return base * 100 / 107 // รวมใน: ถอด VAT
  return base
}

/** ภาษีมูลค่าเพิ่ม */
export function saleVat (item) {
  const base = saleBase(item)
  const t = Number(item.bill_tax_type)
  if (t === 1) return base * 0.07
  if (t === 2) return base * 7 / 107
  return 0
}

/** ยอดขายเต็มรวม VAT */
export function saleTotal (item) {
  const base = saleBase(item)
  if (Number(item.bill_tax_type) === 1) return base * 1.07 // แยกนอก: บวก 7%
  return base // type 0 (ไม่มี VAT) / type 2 (ฐานรวม VAT แล้ว)
}

/* ───────── ยอดคงค้างสุทธิ (outstanding หลังหักมัดจำ) ─────────
 * ยึด bill_grand_total เป็น "ยอดคงค้างจริง" (computeVat คำนวณหักมัดจำ+VAT ให้แล้ว)
 * แล้ว derive breakdown ย้อนจากตรงนั้น → ก่อน VAT + VAT = ยอดคงค้าง เสมอ (ไม่มั่ว)
 * อย่าใช้ raw bill_before_vat/bill_value_added_tax (เป็น full ของยอดขาย ไม่ใช่ของยอดคงค้าง)
 */

/** ยอดคงค้างจริง (รวม VAT) */
export function outstandingTotal (item) {
  return num(item.bill_grand_total)
}

/** ยอดคงค้างก่อน VAT */
export function outstandingBeforeVat (item) {
  const t = Number(item.bill_tax_type)
  const grand = outstandingTotal(item)
  if (t === 1) return grand / 1.07     // แยกนอก: ถอด VAT
  if (t === 2) return grand * 100 / 107 // รวมใน: ถอด VAT
  return grand                          // ไม่มี VAT
}

/** VAT ของยอดคงค้าง */
export function outstandingVat (item) {
  return outstandingTotal(item) - outstandingBeforeVat(item)
}

/** ยอดมัดจำที่หักจริง — backend เก็บ bill_deposit_amt ในฐาน VAT เดียวกับใบส่งของแล้ว
 *  (tax 1 → ถอด VAT, tax 2/0 → รวม VAT) จึงใช้ตรง ๆ ไม่ derive ย้อนจาก grand (ที่อาจเพี้ยน)
 *  fallback: ใบเก่าที่ bill_deposit_amt ว่าง → derive จากผลต่างยอดขาย − คงค้าง */
export function depositBeforeVat (item) {
  const stored = num(item.bill_deposit_amt)
  if (stored > 0) return stored
  return saleBase(item) - outstandingBeforeVat(item)
}

export default {
  saleBase, saleSubtotal, saleBeforeVat, saleVat, saleTotal,
  outstandingTotal, outstandingBeforeVat, outstandingVat, depositBeforeVat
}
